import React, { useEffect } from 'react';
import { motion } from 'framer-motion';
import { Link as RouterLink } from 'react-router-dom';

const Shop = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const upcomingProducts = [
    {
      title: "Allergy-Friendly Recipe E-Books",
      description: "Comprehensive recipe collections for various dietary needs including egg-free, dairy-free, low-oxalate, and anti-inflammatory options.",
      icon: "📚"
    },
    {
      title: "Meal Prep & Planning Guides",
      description: "Step-by-step guides to help you plan and prepare nourishing meals for the whole week.",
      icon: "🥗"
    },
    {
      title: "Eczema & Skin Health Resources",
      description: "Expert guides on managing eczema naturally through diet, lifestyle, and gentle skincare routines.",
      icon: "✨"
    },
    {
      title: "Fitness & Movement Programs",
      description: "Downloadable workout programs for all levels, including gentle options for those with chronic conditions.",
      icon: "🧘‍♀️"
    },
    {
      title: "Wellness Journals & Trackers",
      description: "Printable journals to track your symptoms, meals, moods, and progress on your healing journey.",
      icon: "📝"
    },
    {
      title: "Seasonal Wellness Bundles",
      description: "Curated collections of recipes, tips, and guides tailored to each season's unique wellness needs.",
      icon: "🌸"
    }
  ];

  return (
    <div className="min-h-screen bg-white">
      {/* Hero Section */}
      <section className="relative pt-24 pb-12 bg-gradient-to-br from-sage-50 via-beige-50 to-rose-50">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.div
            initial={{ opacity: 0, y: 50 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            <h1 className="text-3xl sm:text-4xl md:text-5xl font-playfair font-medium text-charcoal-900 mb-4">
              Digital Wellness Shop
            </h1>
            <div className="inline-block px-6 py-3 rounded-full text-lg font-semibold" style={{ backgroundColor: '#d9a79c', color: 'white' }}>
              Coming Soon
            </div>
          </motion.div>
        </div>
      </section>

      {/* What to Expect Section */}
      <section className="py-20 bg-white">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-playfair font-medium text-charcoal-900 mb-4">
              What to Expect
            </h2>
            <div className="h-1 w-24 mx-auto mb-6 rounded-full" style={{ backgroundColor: '#d9a79c' }}></div>
            <p className="text-lg text-charcoal-600 max-w-2xl mx-auto">
              Here's a sneak peek at the digital products we're creating for you
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {upcomingProducts.map((product, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                viewport={{ once: true }}
                className="bg-gradient-to-br from-white to-beige-50 rounded-2xl p-6 shadow-soft hover:shadow-soft-lg transition-all duration-300 border border-beige-100"
              >
                <div className="text-4xl mb-4">{product.icon}</div>
                <h3 className="text-xl font-semibold text-charcoal-900 mb-3">{product.title}</h3>
                <p className="text-charcoal-600 leading-relaxed">{product.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Notify Section */}
      <section className="py-20 bg-gradient-to-br from-rose-50 to-beige-50">
        <div className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
          >
            <h2 className="text-2xl sm:text-3xl font-playfair font-medium text-charcoal-900 mb-4">
              Be the First to Know
            </h2>
            <p className="text-lg text-charcoal-600 mb-8">
              Join our community to get notified when our digital products launch, 
              plus receive exclusive early-bird discounts.
            </p>
            <RouterLink to="/signup">
              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                style={{ backgroundColor: '#d9a79c' }}
                className="text-white px-8 py-4 rounded-full text-lg font-medium hover:opacity-90 transition-all duration-200 shadow-soft hover:shadow-soft-lg"
              >
                Join the Alera Community
              </motion.button>
            </RouterLink>
          </motion.div>
        </div>
      </section>

      {/* Back to Home */}
      <section className="py-12 bg-white">
        <div className="max-w-4xl mx-auto px-4 text-center">
          <RouterLink
            to="/"
            className="text-charcoal-500 hover:text-charcoal-700 transition-colors duration-200"
          >
            ← Back to Home
          </RouterLink>
        </div>
      </section>
    </div>
  );
};

export default Shop;
